import React from "react";
import type { TFunction } from "i18next";
import { Request } from "@/types/request";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import { PriorityStar } from "@/components/common/PriorityStar";
import { CircleCheck, CircleX } from "lucide-react";
import { BillingPaymentStatus } from "@/types/billing-transactions";
import { getTaskAssignment } from "@/components/pages/requests/utils/taskAssignment";
import { Task } from "@/types/tasks";
import { useStaffById } from "@/components/pages/staff/hooks/useStaff";
import { useTeamById } from "@/components/pages/staff/hooks/useTeams";
import { RequestActionsCell } from "@/components/pages/reservations/components/cells/RequestActionsCell";
import { getEffectiveRequestPaymentStatus } from "@/components/pages/requests/utils/paymentStatus";
import { RequestGuestName } from "../RequestGuestName";
import ReactCountryFlag from "react-country-flag";
import { getRequestDisplayTitle } from "../../utils/requestDisplay";
import { isRequestOverdue } from "../../utils/requestDelay";
import { RequestDelayBadge } from "../RequestDelayBadge";

const KanbanCardItem: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
}> = ({ label, value, className }) => {
  return (
    <div className={cn("flex items-center justify-between gap-1", className)}>
      <span className="text-muted-foreground text-xs">{label}</span>
      {typeof value === "string" ? (
        <span className="line-clamp-2 text-xs font-bold break-words text-black">
          {value}
        </span>
      ) : (
        value
      )}
    </div>
  );
};

const countryCodeFromLanguageTag = (language: string | null): string => {
  if (!language) {
    return "";
  }
  const trimmed = language.trim();
  const segments = trimmed.split("-");
  const last = segments[segments.length - 1];
  if (last?.length === 2) {
    return last.toUpperCase();
  }
  if (trimmed.length >= 2) {
    return trimmed.slice(0, 2).toUpperCase();
  }
  return "";
};

const KanbanCardGuestItem: React.FC<{
  label: string;
  guestProfile: {
    firstName: string | null;
    lastName: string | null;
  };
  guestFlagCountryCode: string;
}> = ({ label, guestProfile, guestFlagCountryCode }) => (
  <KanbanCardItem
    label={label}
    value={
      <div className="flex min-w-0 items-center justify-end gap-2">
        {guestFlagCountryCode ? (
          <ReactCountryFlag
            countryCode={guestFlagCountryCode}
            svg
            className="h-fit w-fit shrink-0"
          />
        ) : (
          <span>{guestFlagCountryCode}</span>
        )}
        <RequestGuestName
          firstName={guestProfile.firstName}
          lastName={guestProfile.lastName}
          nameClassName="justify-end w-fit line-clamp-2 break-words font-bold"
        />
      </div>
    }
    className="w-full"
  />
);

type KanbanRoomCategoryDisplay = { name: string; pmsType?: string };
type KanbanStaffDisplay = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
};

const getKanbanAssignedName = ({
  assignedTeamId,
  assignedTeamName,
  assignedStaffId,
  assignedStaffName,
  teamData,
  staffData,
  t,
}: {
  assignedTeamId?: string | null;
  assignedTeamName?: string | null;
  assignedStaffId?: string | null;
  assignedStaffName?: string | null;
  teamData?: { name?: string | null } | null;
  staffData?: KanbanStaffDisplay | null;
  t: TFunction;
}): string => {
  if (assignedTeamName) {
    return assignedTeamName;
  }
  if (teamData?.name) {
    return teamData.name;
  }
  if (assignedTeamId) {
    return t("requests.kanban.teamWithId", { id: assignedTeamId });
  }
  if (assignedStaffName) {
    return assignedStaffName;
  }
  if (staffData) {
    const fullName =
      `${staffData.firstName || ""} ${staffData.lastName || ""}`.trim();
    return (
      fullName ||
      staffData.email ||
      t("requests.kanban.staffWithId", { id: assignedStaffId })
    );
  }
  if (assignedStaffId) {
    return t("requests.kanban.staffWithId", { id: assignedStaffId });
  }
  return "";
};

const getRequestAssignedStaffIds = (
  assignedToStaffId: Request["assignedToStaffId"],
): string[] => {
  if (Array.isArray(assignedToStaffId)) {
    return assignedToStaffId;
  }

  return assignedToStaffId ? [assignedToStaffId] : [];
};

const KanbanRoomCardItem: React.FC<{
  label: string;
  roomNumber: number;
  roomCategory?: KanbanRoomCategoryDisplay;
  className?: string;
}> = ({ label, roomNumber, roomCategory, className }) => (
  <div
    className={cn("flex w-full items-start justify-between gap-2", className)}
  >
    <span className="text-muted-foreground shrink-0 text-xs">{label}</span>
    <div className="flex min-w-0 flex-1 flex-col items-end gap-0.5 text-right">
      <span className="text-xs font-bold text-black">{String(roomNumber)}</span>
      {roomCategory?.name ? (
        <span className="line-clamp-2 max-w-full text-xs font-bold break-words text-black">
          {roomCategory.name}
        </span>
      ) : null}
      {roomCategory?.pmsType ? (
        <span className="max-w-full text-xs font-bold break-words text-black">
          {roomCategory.pmsType}
        </span>
      ) : null}
    </div>
  </div>
);

const KanbanPaymentStatusItem: React.FC<{
  label: string;
  paymentStatus: Request["paymentStatus"];
}> = ({ label, paymentStatus }) => {
  if (paymentStatus === BillingPaymentStatus.NOT_REQUIRED) {
    return (
      <KanbanCardItem
        label={label}
        value={<span className="h-5 w-5" aria-hidden />}
        className="w-full"
      />
    );
  }

  return (
    <KanbanCardItem
      label={label}
      value={
        paymentStatus === BillingPaymentStatus.SUCCEEDED ? (
          <CircleCheck className="text-green-500" />
        ) : (
          <CircleX className="text-red-500" />
        )
      }
      className="w-full"
    />
  );
};

const KanbanCardContent: React.FC<{
  request: Request;
  roomCategory?: KanbanRoomCategoryDisplay;
  onUpdateRequest: (id: string, data: Partial<Request>) => void;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}> = ({ request, roomCategory, onUpdateRequest, onEditRequest }) => {
  const { t } = useTranslation();
  const guestProfile =
    request.guest?.profile || request.booking?.guest?.profile;
  const guestFlagCountryCode = countryCodeFromLanguageTag(
    guestProfile?.language ?? null,
  );
  const paymentStatus = getEffectiveRequestPaymentStatus(request);
  const isOverdue = isRequestOverdue(request);

  const task: Task | undefined = request.tasks?.[0];
  const {
    assignedTeamId,
    assignedTeamName,
    assignedStaffId,
    assignedStaffName,
  } = getTaskAssignment(task, request.service);
  const hasAssignment = Boolean(assignedTeamId || assignedStaffId);

  const shouldFetchTeam = Boolean(assignedTeamId && !assignedTeamName);
  const shouldFetchStaff = Boolean(assignedStaffId && !assignedStaffName);

  const { data: teamData } = useTeamById(
    shouldFetchTeam ? assignedTeamId?.toString() || "" : "",
  );
  const { data: staffData } = useStaffById(
    shouldFetchStaff ? assignedStaffId?.toString() || "" : "",
  );

  const resolvedAssignedName = getKanbanAssignedName({
    assignedTeamId,
    assignedTeamName,
    assignedStaffId,
    assignedStaffName,
    teamData,
    staffData,
    t,
  });
  const assignedToStaffIds = getRequestAssignedStaffIds(
    request.assignedToStaffId,
  );

  return (
    <>
      <div className="mb-2 flex items-start justify-between gap-1">
        <PriorityStar
          priority={Boolean(request.priority)}
          id={request.id || ""}
          onPriorityChange={(id, priority) => onUpdateRequest(id, { priority })}
          starClassName="h-3.5 w-3.5 text-amber-400 shrink-0 mt-0.5"
          priorityStarClassName="fill-amber-400 text-amber-400"
        />
        <span className="line-clamp-2 flex-1 text-sm leading-snug font-medium">
          {getRequestDisplayTitle(request, t("requests.form.noService"))}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-8 gap-y-2">
        <div className="space-y-2">
          {guestProfile ? (
            <KanbanCardGuestItem
              label={t("requests.kanban.guest")}
              guestProfile={guestProfile}
              guestFlagCountryCode={guestFlagCountryCode || ""}
            />
          ) : null}
          {request.createdAt ? (
            <KanbanCardItem
              label={t("requests.kanban.creation")}
              value={format(new Date(request.createdAt), "dd.MM.yyyy HH:mm")}
              className="w-full"
            />
          ) : null}
          <KanbanCardItem
            label={t("requests.kanban.assignedStaff")}
            value={
              hasAssignment && resolvedAssignedName ? (
                resolvedAssignedName
              ) : (
                <CircleX className="text-red-500" />
              )
            }
            className="w-full"
          />
          <KanbanPaymentStatusItem
            label={t("requests.kanban.paymentStatus")}
            paymentStatus={paymentStatus}
          />
        </div>
        <div className="space-y-2">
          {request.room?.number != null ? (
            <KanbanRoomCardItem
              label={t("requests.kanban.room")}
              roomNumber={request.room.number}
              roomCategory={roomCategory}
            />
          ) : null}
          {request.dueDateTime ? (
            <KanbanCardItem
              label={t("requests.kanban.dueDate")}
              value={
                <div className="flex flex-col items-end gap-1">
                  <span
                    className={cn(
                      "text-xs font-bold text-black",
                      isOverdue && "text-red-600",
                    )}
                  >
                    {format(new Date(request.dueDateTime), "dd.MM.yyyy HH:mm")}
                  </span>
                  {isOverdue ? (
                    <RequestDelayBadge className="px-1.5 py-0 text-[10px]" />
                  ) : null}
                </div>
              }
              className="w-full"
            />
          ) : null}
          <KanbanCardItem
            label={t("requests.kanban.createdBy")}
            value={
              request.createdByUserProfile
                ? `${request.createdByUserProfile.firstName ?? ""} ${request.createdByUserProfile.lastName ?? ""}`.trim()
                : ""
            }
            className="w-full"
          />
          <KanbanCardItem
            label={t("requests.table.actions")}
            value={
              <div
                className="flex justify-end"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <RequestActionsCell
                  bookingId={request.bookingId || ""}
                  assignedToStaffIds={assignedToStaffIds}
                  row={request}
                  onEditRequest={onEditRequest}
                  showChatAction={true}
                  isChatWithGuest={true}
                />
              </div>
            }
            className="w-full gap-1"
          />
        </div>
      </div>
    </>
  );
};

export const KanbanCardOverlay: React.FC<{
  request: Request;
  roomCategory?: KanbanRoomCategoryDisplay;
  onUpdateRequest: (id: string, data: Partial<Request>) => void;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}> = ({ request, roomCategory, onUpdateRequest, onEditRequest }) => (
  <div className="w-full rotate-1 cursor-grabbing rounded-lg border bg-white p-3 shadow-xl select-none">
    <KanbanCardContent
      request={request}
      roomCategory={roomCategory}
      onUpdateRequest={onUpdateRequest}
      onEditRequest={onEditRequest}
    />
  </div>
);

export const KanbanCard: React.FC<{
  request: Request;
  roomCategory?: KanbanRoomCategoryDisplay;
  onClick: () => void;
  onUpdateRequest: (id: string, data: Partial<Request>) => void;
  onEditRequest?: (
    request: Request,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}> = ({ request, roomCategory, onClick, onUpdateRequest, onEditRequest }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: request.id,
    });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform) }}
      {...listeners}
      {...attributes}
      className={cn(
        "cursor-grab rounded-lg border bg-white p-3 transition-shadow select-none hover:shadow-md active:cursor-grabbing",
        isDragging && "opacity-30",
      )}
      onClick={onClick}
    >
      <KanbanCardContent
        request={request}
        roomCategory={roomCategory}
        onUpdateRequest={onUpdateRequest}
        onEditRequest={onEditRequest}
      />
    </div>
  );
};
